import firebase from "firebase"
import {db, storage} from "../db";
import { checkSurvey } from "../GeminiService";
import store from "../store";

// global err function
function err(e) {
  return {success: false, error: e};
}

/**
 * Add a word to the Moderation blacklist database.
 * @param {*} word  word to add to the blacklist (string)
 * @returns Promise resolving to {success: true} or {success: false, error: e}
 */
async function addBlacklistWord(word) {
  return new Promise(async (resolve, reject) => {
    db.collection("ModerationRules").doc("single_rules").get()
    .then(doc => {
      if (doc.exists) {
        db.collection("ModerationRules").doc("single_rules").update({
          words: firebase.firestore.FieldValue.arrayUnion(word)
        })
        .then(() => resolve({success: true}))
        .catch(e => reject(err(e)));
      } else {
        db.collection("ModerationRules").doc("single_rules").set({
          words: [word],
          regexRules: [],
          type : "single",
          category: "general"
        })
        .then(() => resolve({success: true}))
        .catch(e => reject(err(e)));
      } 
    })
    .catch(e => reject(err(e)))
  })
}

/**
 * Add a new map of {name, pattern, id} for a regex rule 
 * to the Moderation blacklist database.
 * 
 * @param {*} regex  regex pattern to add to the blacklist
 * @param {*} name   name of the regex rule
 * @returns Promise resolving to {success: true} or {success: false, error: e}
 */
async function addBlacklistRegex(regex, name) {
  return new Promise(async(resolve, reject) => {
    try {
      new RegExp(regex); // will throw if invalid
    } catch (e) {
      return reject({ success: false, error: `Invalid regex: ${regex}` });
    }
    db.collection("ModerationRules").doc("single_rules").get()
    .then(doc => {
        const newRegex = {
          id: db.collection("tmp").doc().id,  // auto-generate unique ID
          name : name,
          pattern: regex
        };
      if (doc.exists) {
        db.collection("ModerationRules").doc("single_rules").update({
          regexRules: firebase.firestore.FieldValue.arrayUnion(newRegex)
        })
        .then(() => resolve({success: true}))
        .catch(e => reject(err(e)));
      } else {
        db.collection("ModerationRules").doc("single_rules").set({
          words: [],
          regexRules: [newRegex],
          type : "single",
          category: "general"
        })
        .then(() => resolve({success: true}))
        .catch(e => reject(err(e)));
      } 
    })
    .catch(e => reject(err(e)))
  })
}

/**
 * Add a list of words from a file to the Moderation blacklist database.
 * The words will be added to the separate document with the given fileName.
 * If the document with the given fileName does not exist, it will be created.
 * @param {*} wordList  list of words to add to the blacklist
 * @param {*} fileName  name of the file (used as document ID)
 * @returns Promise resolving to {success: true} or {success: false, error: e}
 */
async function addBlacklistWordList(wordList, fileName) {
  return new Promise(async (resolve, reject) => {
    db.collection("ModerationRules").doc(fileName).get()
    .then(doc => {
      if (doc.exists) {
        db.collection("ModerationRules").doc(fileName).update({
          words: firebase.firestore.FieldValue.arrayUnion(...wordList)
        })
        .then(() => resolve({success: true}))
        .catch(e => reject(err(e)));
      } else {
        const fileNameWithoutExtension = fileName.replace(/\.[^/.]+$/, ""); // Remove file extension
        db.collection("ModerationRules").doc(fileName).set({
          words: [...wordList],
          regexRules: [],
          type : "file",
          category: fileNameWithoutExtension
        })
        .then(() => resolve({success: true}))
        .catch(e => reject(err(e)));
      } 
    })
    .catch(e => reject(err(e)))
  })
}

/**
 * Add a list of regexMap rules from a file to the Moderation blacklist database.
 * The regexMap rules will be added to the separate document with the given fileName.
 * If the document with the given fileName does not exist, it will be created.
 * @param {*} regexList  list of {id, name, pattern} maps to add to the blacklist
 * @param {*} fileName   name of the file (used as document ID)
 * @returns Promise resolving to {success: true} or {success: false, error: e}
 */
async function addBlacklistRegexList(regexList, fileName) {
  return new Promise(async (resolve, reject) => {
      const validRegexList = regexList.filter(regex => {
        try {
          new RegExp(regex.pattern); // will throw if invalid
          return true;
        } catch (e) {
          return false;
        }
      });
    if (validRegexList.length === 0) {
      return reject({ success: false, error: "No valid regex provided" });
    }
    validRegexList.forEach((regex) => {
      regex.id = db.collection("tmp").doc().id;  // auto-generate unique ID
    });
    db.collection("ModerationRules").doc(fileName).get()
    .then(doc => {
      if (doc.exists) {
        db.collection("ModerationRules").doc(fileName).update({
          regexRules: firebase.firestore.FieldValue.arrayUnion(...validRegexList)
        })
        .then(() => resolve({success: true}))
        .catch(e => reject(err(e)));
      } else {
        const fileNameWithoutExtension = fileName.replace(/\.[^/.]+$/, ""); // Remove file extension
        db.collection("ModerationRules").doc(fileName).set({
          words: [],
          regexRules: [...validRegexList],
          type : "file",
          category: fileNameWithoutExtension
        })
        .then(() => resolve({success: true}))
        .catch(e => reject(err(e)));
      } 
    })
    .catch(e => reject(err(e)))
  })
}

/**
 * Delete a word from the Moderation blacklist database.
 * By default, deletes from the "single_rules" document.
 * docId can be specified to delete from other documents.
 * @param {*} word  word to delete from the blacklist (string)
 * @param {*} docId document ID from which to delete the word (default: "single_rules")
 * @returns Promise resolving to {success: true} or {success: false, error: e}
 */
async function deleteBlacklistWord(word, docId="single_rules") {
  return new Promise(async (resolve, reject) => {
    db.collection("ModerationRules").doc(docId).get()
    .then(() => {
      db.collection("ModerationRules").doc(docId).update({
        words: firebase.firestore.FieldValue.arrayRemove(word)
      })
      .then(() => {
        resolve({success: true});
      })
      .catch(e => reject(err(e)));
    })
    .catch(e => reject(err(e)));
  });
}

/**
 * Delete a regexMap rule from the Moderation blacklist database.
 * By default, deletes from the "single_rules" document.
 * docId can be specified to delete from other documents.
 * @param {*} regexMap  {id, name, pattern} map to delete from the blacklist
 * @param {*} docId     document ID from which to delete the regexMap (default: "single_rules")
 * @returns Promise resolving to {success: true} or {success: false, error: e}
 */
async function deleteBlacklistRegex(regexMap, docId="single_rules") {
  return new Promise(async (resolve, reject) => {
    db.collection("ModerationRules").doc(docId).get()
    .then(() => {
      db.collection("ModerationRules").doc(docId).update({
        regexRules: firebase.firestore.FieldValue.arrayRemove(regexMap)
      })
      .then(() => {
        resolve({success: true});
      })
      .catch(e => reject(err(e)));
    })
    .catch(e => reject(err(e)));
  });
}


/**
 * Sets up a listener on the database collection "ModerationRules", so whenever
 * "ModerationRules" changes in the database, a callback function can be called to update data.
 *
 * @param {function} callback - A function to be called with the updated data. The callback receives
 * an object with the following structure:
 *   - {boolean} success - Indicates whether the operation was successful.
 *   - {Array<Object>} data - An array of moderation rule objects, each containing:
 *       - {string} docID - The document ID of the moderation rule.
 *       - {string} category - The category of the moderation rule.
 *       - {Array<string>} regexRules - An array of regexMap rules.
 *       - {string} type - The type of the moderation rule.
 *       - {Array<string>} words - An array of words associated with the moderation rule.
 * If the collection is empty, `data` will be an empty array.
 *
 * @returns {function} - A function to unsubscribe from the listener.
 */
function watchAllModerationRules(callback) {
  return db.collection("ModerationRules").onSnapshot(querySnapshot => {
    if (querySnapshot.empty) {
        callback({success: true, data: []}) // There are no moderation rules in the DB
      } else {
        let rules = [];
        querySnapshot.forEach(doc => {
          let newDoc = {
            docID: doc.id,
            category: doc.data().category,
            regexRules: doc.data().regexRules,
            type: doc.data().type,
            words: doc.data().words,
          };
          rules.push(newDoc);
        });
        callback({success: true, data: rules});
      }
  }, e => callback(err(e)));
}

/** 
 * Moderate a survey's questions against the blacklist rules in the database.
 * If any question matches a blacklist rule, the promise is rejected with the question flagged and the reason.
 * 
 * @param {*} survey  survey contains an array of questions to moderate
 * @returns Promise resolving to {success: true, data: survey.questions} if no matches found,
 *          or rejecting with {success: false, error: reason, question} if a match is found.
*/
async function moderateSurvey(survey) {
  return new Promise(async (resolve, reject) => {
    if (survey.questions.length === 0) {
      return resolve({success: true, data: survey.questions}); // No questions to moderate
    }
    let results = [];
    db.collection("ModerationRules").get()
    .then(snapshot => {
      if (snapshot.empty) {
        return resolve({success: true, data: survey.questions}); // No rules to moderate against
      }

      for (const doc of snapshot.docs) {
        const words = doc.data().words;
        let regexRules = doc.data().regexRules.map(r => ({
          name: r.name,
          pattern: new RegExp(r.pattern)
        }));
        for (const question of survey.questions) {
          let result = checkQuestion(question, words, regexRules);
          console.log("Moderation each question:", result);

          if (result) {
            console.log("Moderation match found:", result);
            results.push([result, question]);
          }
        }
      }
      if (results.length > 0) {
        return reject({success: false, error: `Questions flagged`, results: results});  
      }
      return resolve({success: true, data: survey.questions}); // No matches found
    })
    .catch(e => reject(err(e)));
  });
}


/**
 * Moderate a text against the blacklist rules in the database.
 * If any text matches a blacklist rule, the promise is rejected with the text flagged and the reason.
 * 
 * @param {*} text  text to check
 * @returns Promise
 **/
async function moderateText(text) {
  if (!text.trim()) {
    return { success: true }; // No questions to moderate
  }

  const snapshot = await db.collection("ModerationRules").get();

  if (snapshot.empty) {
    return { success: true }; // No rules to moderate against
  }

  for (const doc of snapshot.docs) {
    const words = doc.data().words;
    const regexRules = doc.data().regexRules.map(r => ({
      name: r.name,
      pattern: new RegExp(r.pattern)
    }));
    
    const result = checkText(text, words, regexRules);
    if (result) {
      return {
        success: false,
        error: `Text flagged due to ${result.type}: ${result.value}`
      };
    }
  }

  return { success: true }; // No matches found
}

/**
 * A helper function to check a single question against given words and regex rules.
 *
 * @param {*} question  question to check
 * @param {*} words   list of words to check against
 * @param {*} regexRules  list of {name, pattern} regex rules to check against
 * @returns An object {matched: true, type: "word"|"regex", value: matchedValue} if a match is found
 */


function checkText(text, words, regexRules) {

  const lowerText = text.toLowerCase();
  const badWord = words.find(word => lowerText.includes(word.toLowerCase()));
  if (badWord) {
    return { matched: true, type: "word", value: badWord };
  }

  const badRegex = regexRules.find(r => r.pattern.test(lowerText));
  if (badRegex) {
    return { matched: true, type: "regex", value: badRegex };
  }
}
function checkQuestion(question, words, regexRules) {
  const content = question.content;
  console.log("Checking question:", question);
  console.log("Against words:", words);
  console.log("Against regexRules:", regexRules);

  const lowerTitle = content.title.toLowerCase();
  const badWord = words.find(word => lowerTitle.includes(word.toLowerCase()));
  if (badWord) {
    question.flagged = true;
    question.flagReason = `Word: "${badWord}"`;
    return { matched: true, type: "word", value: badWord };
  }
  const badRegex = regexRules.find(r => r.pattern.test(lowerTitle));
  if (badRegex) {
    question.flagged = true;
    question.flagReason = `Pattern: "${badRegex.name}"`;
    return { matched: true, type: "regex", value: badRegex };
  }

  switch (question.qType) {
    case "multi-choice":
      for (let option of content.options) {
        const lowerText = option.text.toLowerCase();
        const badWord = words.find(word => lowerText.includes(word.toLowerCase()));
        if (badWord) {
          question.flagged = true;
          question.flagReason = `Word: "${badWord}"`;
          return { matched: true, type: "word", value: badWord };
        }

        const badRegex = regexRules.find(r => r.pattern.test(lowerText));
        if (badRegex) {
          question.flagged = true;
          question.flagReason = `Pattern: "${badRegex.name}"`;
          return { matched: true, type: "regex", value: badRegex };
        }
      }
      break;

    case "multi-select":
      for (let option of content.options) {
        const lowerText = option.toLowerCase();
        const badWord = words.find(word => lowerText.includes(word.toLowerCase()));
        if (badWord) {
          question.flagged = true;
          question.flagReason = `Word: "${badWord}"`;
          return { matched: true, type: "word", value: badWord };
        }

        const badRegex = regexRules.find(r => r.pattern.test(lowerText));
        if (badRegex) {
          question.flagged = true;
          question.flagReason = `Pattern: "${badRegex.name}"`;
          return { matched: true, type: "regex", value: badRegex };
        }
      }
      break;

    case "dropdown":
      for (let option of content.options) {
        const lowerText = option.toLowerCase();
        const badWord = words.find(word => lowerText.includes(word.toLowerCase()));
        if (badWord) {
          question.flagged = true;
          question.flagReason = `Word: "${badWord}"`;
          return { matched: true, type: "word", value: badWord };
        }

        const badRegex = regexRules.find(r => r.pattern.test(lowerText));
        if (badRegex) {
          question.flagged = true;
          question.flagReason = `Pattern: "${badRegex.name}"`;
          return { matched: true, type: "regex", value: badRegex };
        }
      }
      break;

    case "text-answer":
      break;

    case "linear-scale":
      const lowerEnd = content.endLabel.toLowerCase();
      const lowerStart = content.startLabel.toLowerCase();
      const badWordEnd = words.find(word => lowerEnd.includes(word.toLowerCase()));
      if (badWordEnd) {
        question.flagged = true;
        question.flagReason = `Word: "${badWordEnd}"`;
        return { matched: true, type: "word", value: badWordEnd };
      }
      const badRegexEnd = regexRules.find(r => r.pattern.test(lowerEnd));
      if (badRegexEnd) {
        question.flagged = true;
        question.flagReason = `Pattern: "${badRegexEnd.name}"`;
        return { matched: true, type: "regex", value: badRegexEnd };
      }
      const badWordStart = words.find(word => lowerStart.includes(word.toLowerCase()));
      if (badWordStart) {
        question.flagged = true;
        question.flagReason = `Word: "${badWordStart}"`;
        return { matched: true, type: "word", value: badWordStart };
      }
      const badRegexStart = regexRules.find(r => r.pattern.test(lowerStart));
      if (badRegexStart) {
        question.flagged = true;
        question.flagReason = `Pattern: "${badRegexStart.name}"`;
        return { matched: true, type: "regex", value: badRegexStart };
      }
      break;
  }
}

/**
 * TASK 2: Moderate Survey with AI (Client-Side)
 *
 * Calls the client-side Gemini service to check survey
 *
 * @param {object} survey - The survey with questions to moderate
 * @param {string} surveyId - The survey ID
 * @param {string} clientId - The client ID (use 'default-client' for now)
 * @returns Promise resolving if SAFE, rejecting if FLAGGED
 */
async function moderateSurveyWithAI(survey, surveyId, clientId) {
  return new Promise(async (resolve, reject) => {
    if (!survey || !survey.questions || survey.questions.length === 0) {
      return resolve({success: true, data: {status: 'SAFE', flagged_questions: []}});
    }

    try {
      // Call client-side Gemini service
      // Try store first, then localStorage, then clientId
      const userId = (store.state && store.state.authId) || window.localStorage.getItem('userId') || clientId || 'default-client';
      console.log('🔍 [Moderation.js] Using userId:', userId);
      console.log('🔍 [Moderation.js] store.state.authId:', store.state && store.state.authId);
      console.log('🔍 [Moderation.js] localStorage userId:', window.localStorage.getItem('userId'));
      console.log('🔍 [Moderation.js] clientId:', clientId);
      const result = await checkSurvey(userId, survey.name || 'Untitled', survey.questions);

      console.log('🤖 AI moderation result:', result);

      // If AI flagged the survey, mark the questions and reject
      if (result.status === 'FLAGGED') {
        // Mark flagged questions with flagged: true and flagReason
        if (result.flagged_questions && result.flagged_questions.length > 0) {
          result.flagged_questions.forEach(flagged => {
            const question = survey.questions[flagged.index];
            if (question) {
              question.flagged = true;
              question.flagReason = `🤖 AI: ${flagged.reason}`;
            }
          });
        }

        return reject({
          success: false,
          error: `🤖 AI flagged: ${result.overall_reason}`,
          flagged_questions: result.flagged_questions || []
        });
      }

      // Survey passed AI moderation
      resolve({success: true, data: result});

    } catch (error) {
      console.error('❌ AI moderation error:', error);
      // Graceful fallback - don't block publishing if AI fails
      console.warn('⚠️ AI moderation unavailable, allowing survey to proceed');
      resolve({success: true, data: {status: 'SAFE', message: 'AI unavailable'}});
    }
  });
}

export default {
  addBlacklistWord,
  addBlacklistRegex,
  addBlacklistWordList,
  addBlacklistRegexList,
  deleteBlacklistWord,
  deleteBlacklistRegex,
  watchAllModerationRules,
  moderateSurvey,
  moderateText,
  moderateSurveyWithAI,
}



// WEBPACK FOOTER //
// ./src/Firestore/Moderation.js